import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';

import {
  Coupon,
  CouponSearchResult,
  PurchaseCoupon,
  SubscriptionCoupon,
} from './_types';
import {
  denormalizePurchaseCoupon,
  denormalizeSubscriptionCoupon,
  normalizeCouponSearchResult,
  normalizePurchaseCoupon,
  normalizeStripeCoupon,
  normalizeSubscriptionCoupon,
} from './normalizers';
interface SearchCouponsOptions {
  search: string;
  couponTypes: ('coupon_purchase' | 'coupon_subscription')[];
}

export const searchCoupons = async ({
  search,
  couponTypes,
}: SearchCouponsOptions): Promise<Coupon[]> => {
  const response = await api.get('/sm/search', {
    params: {
      types: couponTypes,
      limit: 20,
      no_limit: false,
      term: search,
      page: 1,
    },
  });
  return response.data.results[couponTypes[0]].map((r: CouponSearchResult) =>
    normalizeCouponSearchResult(r)
  );
};

interface ConfirmCouponResponse {
  message: string;
  valid: number;
}

interface ConfirmCouponOptions {
  couponCode: string;
  type: 'subscription' | 'purchase';
  purchaseableId?: string | number;
  purchaseableType?: string;
}

export const confirmCoupon = async ({
  couponCode,
  type,
  purchaseableId,
  purchaseableType,
}: ConfirmCouponOptions): Promise<ConfirmCouponResponse> => {
  const response = await api.get('/confirm-coupon', {
    params: {
      coupon: couponCode,
      type,
      ...(purchaseableId &&
        purchaseableType && {
          purchaseable: {
            purchaseable_id: purchaseableId,
            purchaseable_type: purchaseableType,
          },
        }),
    },
  });

  return response.data;
};

export const confirmPackageCoupon = async (
  couponCode: string,
  packageId: Identifier
): Promise<ConfirmCouponResponse> => {
  return confirmCoupon({
    couponCode,
    type: 'subscription',
    purchaseableId: packageId,
    purchaseableType: 'Fbm::Package',
  });
};

export const createPurchaseCoupon = async (
  couponData: Partial<PurchaseCoupon>
): Promise<PurchaseCoupon> => {
  const denormalizedData = denormalizePurchaseCoupon(couponData);

  const response = await api.post<PurchaseCoupon>('/api/v2/coupons', {
    coupon: {
      ...denormalizedData,
      coupon_type: 'purchase',
    },
  });

  return normalizePurchaseCoupon(response.data);
};

export const updatePurchaseCoupon = async (
  couponData: Partial<PurchaseCoupon>
): Promise<PurchaseCoupon> => {
  const denormalizedData = denormalizePurchaseCoupon(couponData);

  const response = await api.put<PurchaseCoupon>(
    `/api/v2/coupons/${couponData.id}`,
    {
      coupon: {
        ...denormalizedData,
        coupon_type: 'purchase',
      },
    }
  );

  return normalizePurchaseCoupon(response.data);
};

export const createStripeCoupon = async (
  couponData: Partial<SubscriptionCoupon>
): Promise<SubscriptionCoupon> => {
  const response = await api.post<SubscriptionCoupon>(
    '/api/v2/stripe_coupons',
    { ...denormalizeSubscriptionCoupon(couponData), coupon_type: 'stripe' }
  );
  return normalizeSubscriptionCoupon(response.data);
};

interface FetchCouponsOptions {
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  search?: string;
  page?: number;
  perPage?: number;
  fetchAll?: boolean;
}

interface FetchCouponsResponse {
  coupons: Coupon[];
  total: number;
}

export const fetchStripeCoupons = async (
  options?: FetchCouponsOptions
): Promise<FetchCouponsResponse> => {
  const response = await api.get('/api/v2/stripe_coupons', {
    params: {
      ...(options?.fetchAll
        ? { fetch_all: true }
        : {
            page: options?.page || 1,
            per_page: options?.perPage || 20,
          }),
      q: {
        ...(options?.search && { code_cont: options.search }),
        s: `${options?.sortBy || 'id'} ${options?.sortDir || 'desc'}`,
      },
    },
  });

  // When fetch_all is true, response is a direct array
  if (options?.fetchAll) {
    const coupons = Array.isArray(response.data)
      ? response.data.map(normalizeStripeCoupon)
      : [];
    return { coupons, total: coupons.length };
  }

  return {
    coupons: response.data.coupon.map(normalizeStripeCoupon),
    total: response.data.meta?.total || 0,
  };
};

export const fetchPurchaseCoupons = async (
  options?: FetchCouponsOptions
): Promise<FetchCouponsResponse> => {
  const response = await api.get('/api/v2/coupons', {
    params: {
      ...(options?.fetchAll
        ? { fetch_all: true }
        : {
            page: options?.page || 1,
            per_page: options?.perPage || 20,
          }),
      q: {
        s: `${options?.sortBy || 'id'} ${options?.sortDir || 'desc'}`,
        ...(options?.search && { code_cont: options.search }),
      },
    },
  });

  // When fetch_all is true, response is a direct array
  if (options?.fetchAll) {
    const coupons = Array.isArray(response.data)
      ? response.data.map(normalizePurchaseCoupon)
      : [];
    return { coupons, total: coupons.length };
  }

  return {
    coupons: response.data.coupon.map(normalizePurchaseCoupon),
    total: response.data.meta?.total || 0,
  };
};

export const deleteCoupon = async (couponId: string): Promise<void> => {
  await api.delete(`/api/v2/coupons/${encodeURIComponent(couponId)}`);
};

export const deleteStripeCoupon = async (couponId: string): Promise<void> => {
  await api.delete(`/api/v2/stripe_coupons/${encodeURIComponent(couponId)}`);
};
